import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { ActivityScheduleSimpleService } from '../../services/activity-schedule-simple.service';
import { AuthService } from 'src/app/services/auth.service';

interface TimeSlot {
  start_time: string;
  end_time: string;
  notes?: string;
}

interface DayAvailability {
  day_of_week: number;
  enabled: boolean;
  slots: TimeSlot[];
}

@Component({
  selector: 'app-availability-management-simple',
  templateUrl: './availability-management-simple.component.html',
  styleUrls: ['./availability-management.component.scss']
})
export class AvailabilityManagementSimpleComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  availabilityForm!: FormGroup;
  currentUserId = 1; // Simplified for demo
  isLoading = false;
  isSaving = false;
  
  dayNames = [
    { id: 1, name: 'Luni', short: 'L' },
    { id: 2, name: 'Marți', short: 'Ma' },
    { id: 3, name: 'Miercuri', short: 'Mi' },
    { id: 4, name: 'Joi', short: 'J' },
    { id: 5, name: 'Vineri', short: 'V' },
    { id: 6, name: 'Sâmbătă', short: 'S' },
    { id: 7, name: 'Duminică', short: 'D' }
  ];

  timeSlots: string[] = [];
  
  // Default working hours
  defaultWorkingHours = {
    start: '09:00',
    end: '17:00'
  };

  activityTypes = [
    { id: 1, name: 'Vizită la domiciliu', active: true },
    { id: 2, name: 'Întâlnire birou', active: true },
    { id: 3, name: 'Evaluare copil', active: true },
    { id: 4, name: 'Ședință echipă', active: true },
    { id: 5, name: 'Consultanță', active: true }
  ];

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private activityScheduleService: ActivityScheduleSimpleService,
    private authService: AuthService
  ) {
    this.generateTimeSlots();
  }

  async ngOnInit(): Promise<void> {
    await this.loadUserContext();
    this.initializeForm();
    this.loadCurrentAvailability();
  }

  private async loadUserContext(): Promise<void> {
    try {
      const user = await this.authService.getCurrentUser();
      console.log('Current user from AuthService:', user); // Debug log
      
      if (user && user.id_user) {
        this.currentUserId = user.id_user;
      } else if (user && user.id) {
        this.currentUserId = user.id; // Fallback to 'id' property
      } else {
        console.warn('No user ID found in user object:', user);
        // For testing purposes, use user ID 1 if no user found
        this.currentUserId = 1;
      }
      
      console.log('Set currentUserId to:', this.currentUserId); // Debug log
    } catch (error) {
      console.error('Error loading current user:', error);
      // Fallback to user ID 1 for testing
      this.currentUserId = 1;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private generateTimeSlots(): void {
    for (let hour = 8; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        this.timeSlots.push(timeStr);
      }
    }
  }

  private initializeForm(): void {
    const daysArray = this.fb.array<FormGroup>([]);
    
    // Create form controls for each day of the week
    for (let day = 1; day <= 7; day++) {
      daysArray.push(this.createDayFormGroup(day));
    }
    
    this.availabilityForm = this.fb.group({
      days: daysArray,
      // Activity type preferences
      enabledActivityTypes: this.fb.array(this.activityTypes.map(type => 
        this.fb.control(type.active)
      )),
      // General settings
      allowBookingOutsideHours: [false],
      requireApprovalForChanges: [true],
      notificationPreferences: this.fb.group({
        email: [true],
        sms: [false],
        inApp: [true]
      })
    });
  }

  private createDayFormGroup(dayOfWeek: number): FormGroup {
    return this.fb.group({
      day_of_week: [dayOfWeek],
      enabled: [dayOfWeek >= 1 && dayOfWeek <= 5], // Default: Mon-Fri enabled
      slots: this.fb.array([
        this.createTimeSlotFormGroup() // Default single slot
      ])
    });
  }

  private createTimeSlotFormGroup(): FormGroup {
    return this.fb.group({
      start_time: [this.defaultWorkingHours.start, Validators.required],
      end_time: [this.defaultWorkingHours.end, Validators.required],
      notes: ['']
    }, { validators: this.timeSlotValidator.bind(this) });
  }

  private timeSlotValidator(group: FormGroup) {
    const startTime = group.get('start_time')?.value;
    const endTime = group.get('end_time')?.value;
    
    if (!startTime || !endTime) return null;
    
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    
    if (start >= end) {
      return { timeRange: 'Ora de început trebuie să fie înainte de ora de sfârșit' };
    }
    
    return null;
  }

  private loadCurrentAvailability(): void {
    if (!this.currentUserId) {
      console.warn('No current user ID available, cannot load availability');
      return;
    }
    
    this.isLoading = true;
    console.log('Loading availability for user ID:', this.currentUserId); // Debug log
    
    this.activityScheduleService.getUserAvailability(this.currentUserId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (availability) => {
        console.log('Loaded availability:', availability); // Debug log
        this.populateFormWithData(availability);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading availability:', error); // Debug log
        // If no availability found, use defaults (don't show error)
        console.log('No saved availability found, using defaults');
        this.isLoading = false;
      }
    });
  }

  private populateFormWithData(availability: any): void {
    // Update form with loaded data
    if (availability.days) {
      availability.days.forEach((dayData: any, index: number) => {
        const dayControl = this.getDaysArray().at(index);
        if (dayControl) {
          dayControl.get('enabled')?.setValue(dayData.enabled);
          
          // Clear existing slots
          const slotsArray = dayControl.get('slots') as FormArray;
          while (slotsArray.length > 0) {
            slotsArray.removeAt(0);
          }
          
          // Add loaded slots
          dayData.slots.forEach((slot: TimeSlot) => {
            const slotGroup = this.createTimeSlotFormGroup();
            slotGroup.patchValue(slot);
            slotsArray.push(slotGroup);
          });
        }
      });
    }
  }

  getDaysArray(): FormArray {
    return this.availabilityForm.get('days') as FormArray;
  }

  getDayFormGroup(dayIndex: number): FormGroup {
    return this.getDaysArray().at(dayIndex) as FormGroup;
  }

  getSlotsArray(dayIndex: number): FormArray {
    return this.getDayFormGroup(dayIndex).get('slots') as FormArray;
  }

  getDayName(dayOfWeek: number): string {
    return this.dayNames[dayOfWeek - 1]?.name || '';
  }

  isDayEnabled(dayIndex: number): boolean {
    return this.getDayFormGroup(dayIndex).get('enabled')?.value || false;
  }

  toggleDay(dayIndex: number): void {
    const dayGroup = this.getDayFormGroup(dayIndex);
    const currentEnabled = dayGroup.get('enabled')?.value;
    dayGroup.get('enabled')?.setValue(!currentEnabled);
  }

  addTimeSlot(dayIndex: number): void {
    const slotsArray = this.getSlotsArray(dayIndex);
    slotsArray.push(this.createTimeSlotFormGroup());
  }

  removeTimeSlot(dayIndex: number, slotIndex: number): void {
    const slotsArray = this.getSlotsArray(dayIndex);
    if (slotsArray.length > 1) {
      slotsArray.removeAt(slotIndex);
    } else {
      this.notificationService.warningSwal(
        'Atenție', 
        'Trebuie să existe cel puțin un interval de timp pentru zilele active.', 
        'warning'
      );
    }
  }

  applyToAllDays(dayIndex: number): void {
    const sourceDayGroup = this.getDayFormGroup(dayIndex);
    const sourceSlots = sourceDayGroup.get('slots')?.value;
    const sourceEnabled = sourceDayGroup.get('enabled')?.value;

    if (!sourceSlots || sourceSlots.length === 0) {
      this.notificationService.warningSwal(
        'Eroare', 
        'Nu există intervale de timp pentru a fi copiate.', 
        'error'
      );
      return;
    }

    // Apply to all other days
    for (let i = 0; i < 7; i++) {
      if (i !== dayIndex) {
        const targetDayGroup = this.getDayFormGroup(i);
        targetDayGroup.get('enabled')?.setValue(sourceEnabled);
        
        // Clear existing slots
        const targetSlotsArray = this.getSlotsArray(i);
        while (targetSlotsArray.length > 0) {
          targetSlotsArray.removeAt(0);
        }
        
        // Add new slots
        sourceSlots.forEach((slot: TimeSlot) => {
          const newSlotGroup = this.createTimeSlotFormGroup();
          newSlotGroup.patchValue(slot);
          targetSlotsArray.push(newSlotGroup);
        });
      }
    }

    this.notificationService.successSwal(
      'Succes', 
      'Programul a fost aplicat pentru toate zilele!'
    );
  }

  applyWorkingDaysOnly(dayIndex: number): void {
    const sourceDayGroup = this.getDayFormGroup(dayIndex);
    const sourceSlots = sourceDayGroup.get('slots')?.value;
    const sourceEnabled = sourceDayGroup.get('enabled')?.value;

    if (!sourceSlots || sourceSlots.length === 0) {
      this.notificationService.warningSwal(
        'Eroare', 
        'Nu există intervale de timp pentru a fi copiate.', 
        'error'
      );
      return;
    }

    // Apply to Monday-Friday (indices 0-4)
    for (let i = 0; i < 5; i++) {
      if (i !== dayIndex) {
        const targetDayGroup = this.getDayFormGroup(i);
        targetDayGroup.get('enabled')?.setValue(sourceEnabled);
        
        // Clear existing slots
        const targetSlotsArray = this.getSlotsArray(i);
        while (targetSlotsArray.length > 0) {
          targetSlotsArray.removeAt(0);
        }
        
        // Add new slots
        sourceSlots.forEach((slot: TimeSlot) => {
          const newSlotGroup = this.createTimeSlotFormGroup();
          newSlotGroup.patchValue(slot);
          targetSlotsArray.push(newSlotGroup);
        });
      }
    }

    this.notificationService.successSwal(
      'Succes', 
      'Programul a fost aplicat pentru zilele lucrătoare (Luni-Vineri)!'
    );
  }

  validateForm(): boolean {
    if (!this.availabilityForm.valid) {
      this.notificationService.warningSwal(
        'Eroare', 
        'Vă rugăm să corectați erorile din formular.', 
        'error'
      );
      return false;
    }

    // Check if at least one day is enabled with at least one time slot
    const days = this.availabilityForm.get('days')?.value;
    const hasEnabledDay = days.some((day: DayAvailability) => 
      day.enabled && day.slots && day.slots.length > 0
    );

    if (!hasEnabledDay) {
      this.notificationService.warningSwal(
        'Atenție', 
        'Trebuie să existe cel puțin un interval de timp pentru fiecare zi activă.', 
        'warning'
      );
      return false;
    }

    return true;
  }

  saveAvailability(): void {
    if (!this.validateForm()) {
      return;
    }

    if (!this.currentUserId) {
      this.notificationService.warningSwal('Eroare', 'Nu s-a putut identifica utilizatorul curent. Vă rugăm să vă reautentificați.', 'error');
      return;
    }

    this.isSaving = true;
    const formData = this.availabilityForm.value;
    const availabilitySlots: any[] = [];

    // Convert form data to API format
    formData.days.forEach((day: DayAvailability, index: number) => {
      if (day.enabled && day.slots && day.slots.length > 0) {
        day.slots.forEach((slot: TimeSlot) => {
          availabilitySlots.push({
            day_of_week: index + 1,
            start_time: slot.start_time,
            end_time: slot.end_time,
            notes: slot.notes || undefined
          });
        });
      }
    });

    console.log('Saving availability for user ID:', this.currentUserId); // Debug log
    console.log('Availability slots to save:', availabilitySlots); // Debug log

    this.activityScheduleService.saveUserAvailability(this.currentUserId, availabilitySlots).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        console.log('Availability saved successfully:', response); // Debug log
        this.notificationService.successSwal(
          'Succes', 
          'Programul de disponibilitate a fost salvat cu succes!'
        );
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error saving availability:', error); // Debug log
        let errorMessage = 'Eroare necunoscută';
        if (error.message) {
          errorMessage = error.message;
        } else if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        
        this.notificationService.warningSwal(
          'Eroare', 
          'Eroare la salvarea programului: ' + errorMessage, 
          'error'
        );
        this.isSaving = false;
      }
    });
  }

  resetToDefaults(): void {
    this.notificationService.warningSwal(
      'Confirmare',
      'Sunteți sigur că doriți să resetați programul la valorile implicite? Toate modificările vor fi pierdute.',
      'warning'
    ).then((result) => {
      if (result.isConfirmed) {
        this.initializeForm();
        this.notificationService.successSwal(
          'Succes',
          'Programul a fost resetat la valorile implicite!'
        );
      }
    });
  }

  previewSchedule(): void {
    if (!this.validateForm()) {
      return;
    }

    const formData = this.availabilityForm.value;
    let previewText = 'Programul dumneavoastră de disponibilitate:\n\n';

    formData.days.forEach((day: DayAvailability, index: number) => {
      const dayName = this.getDayName(day.day_of_week);
      if (day.enabled && day.slots.length > 0) {
        previewText += `${dayName}:\n`;
        day.slots.forEach((slot: TimeSlot) => {
          previewText += `  • ${slot.start_time} - ${slot.end_time}`;
          if (slot.notes) {
            previewText += ` (${slot.notes})`;
          }
          previewText += '\n';
        });
      } else {
        previewText += `${dayName}: Indisponibil\n`;
      }
    });

    this.notificationService.warningSwal(
      'Previzualizare Program',
      previewText,
      'info'
    );
  }

  getActivityTypesArray(): FormArray {
    return this.availabilityForm.get('enabledActivityTypes') as FormArray;
  }

  toggleActivityType(index: number): void {
    const control = this.getActivityTypesArray().at(index);
    control?.setValue(!control.value);
  }

  isActivityTypeEnabled(index: number): boolean {
    return this.getActivityTypesArray().at(index)?.value || false;
  }
}